/**
 * Retailers directory: region scroll-spy menu and live name search.
 *
 * Was `sections/retailers-list.liquid`'s {% javascript %} tag, shipped on every page
 * through `scripts.js`. Loaded from the section now.
 *
 * Wrapped for the same reason as faq.js: {% javascript %} gave the class its own scope,
 * a bare asset would not.
 */
(function () {
  class RetailersList extends HTMLElement {
    connectedCallback() {
      this.nav = this.querySelector('.retailers__nav');
      this.tabs = Array.from(this.querySelectorAll('.retailers__tab'));
      this.sections = Array.from(this.querySelectorAll('[data-region-section]'));
      this.searchInput = this.querySelector('.retailers__search-input');
      this.emptyState = this.querySelector('[data-empty]');
      this.toTop = this.querySelector('[data-to-top]');
      this.sentinel = this.querySelector('[data-top-sentinel]');

      this.updateNavHeight();
      this.onResize = this.updateNavHeight.bind(this);
      window.addEventListener('resize', this.onResize);

      this.tabs.forEach((tab) => tab.addEventListener('click', this.onTabClick.bind(this)));
      this.setupScrollSpy();
      this.setupBackToTop();

      if (this.searchInput) {
        this.searchInput.addEventListener('input', this.onSearch.bind(this));
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.topObserver) this.topObserver.disconnect();
      if (this.onResize) window.removeEventListener('resize', this.onResize);
    }

    updateNavHeight() {
      if (!this.nav) return;
      this.style.setProperty('--nav-height', this.nav.offsetHeight + 'px');
    }

    setupScrollSpy() {
      if (!this.tabs.length || !this.sections.length) return;
      this.observer = new IntersectionObserver(this.onIntersect.bind(this), {
        rootMargin: '-10px 0px -60% 0px',
        threshold: 0,
      });
      this.sections.forEach((section) => this.observer.observe(section));
    }

    onIntersect(entries) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) this.setActive('#' + entry.target.id);
      });
    }

    setupBackToTop() {
      if (!this.toTop || !this.sentinel) return;
      this.topObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          // Show once the top of the section (sentinel) is scrolled out of view.
          this.toTop.classList.toggle('is-visible', !entry.isIntersecting);
        });
      }, { threshold: 0 });
      this.topObserver.observe(this.sentinel);
      this.toTop.addEventListener('click', () => {
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    onTabClick(event) {
      const href = event.currentTarget.getAttribute('href') || '';
      if (href.charAt(0) !== '#') return;
      const id = href.slice(1);
      const selector = '#' + (window.CSS && CSS.escape ? CSS.escape(id) : id);
      const target = this.querySelector(selector);
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.setActive(href);
    }

    setActive(href) {
      this.tabs.forEach((tab) => {
        tab.classList.toggle('is-active', tab.getAttribute('href') === href);
      });
    }

    tabForSection(section) {
      const href = '#' + section.id;
      return this.tabs.find((tab) => tab.getAttribute('href') === href);
    }

    onSearch() {
      const query = this.searchInput.value.trim().toLowerCase();
      const searching = query.length > 0;
      let anyVisible = false;

      this.sections.forEach((section) => {
        // A matching region name reveals the whole region.
        const regionName = section.getAttribute('data-name') || '';
        const regionMatch = !searching || regionName.indexOf(query) !== -1;
        let sectionHasVisible = false;

        section.querySelectorAll('[data-subregion]').forEach((sub) => {
          // A matching subregion name (or its region) reveals all its stores.
          const subName = sub.getAttribute('data-name') || '';
          const subMatch = regionMatch || subName.indexOf(query) !== -1;
          let subHasVisible = false;

          sub.querySelectorAll('[data-item]').forEach((item) => {
            const name = item.getAttribute('data-name') || '';
            const visible = !searching || subMatch || name.indexOf(query) !== -1;
            item.hidden = !visible;
            if (visible) subHasVisible = true;
          });

          sub.hidden = !subHasVisible;
          if (subHasVisible) sectionHasVisible = true;
        });

        section.hidden = !sectionHasVisible;
        if (sectionHasVisible) anyVisible = true;
        const tab = this.tabForSection(section);
        if (tab) tab.classList.toggle('is-empty', !sectionHasVisible);
      });

      if (this.emptyState) this.emptyState.hidden = !searching || anyVisible;
    }
  }

  if (!customElements.get('retailers-list')) {
    customElements.define('retailers-list', RetailersList);
  }
})();
